import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { getDiaryList, IDiaryListResult, toggleDiaryLike, deleteDiary } from "api/student/v2/diary.api";
import { IDiary } from "api/interfaces/profile.interface";
import { getImageUrl } from "helpers/Image.helper";
import VideoPlayer from "commons/video/VideoPlayer";
import ImageSlidePopup from "pages/common/v2/ImageSlidePopup";
import { usePopup } from "contexts/PopupContext";

const GrowthDiary = () => {
    const navigate = useNavigate();
    const userInfo = useSelector((state: any) => state.user.info);
    const { showPopup } = usePopup();
    // API 응답 interface 직접 사용
    // getDiaryList의 return: IDiaryListResult { data: IDiary[], pagination: IPagination }

    const [isLoading, setIsLoading] = useState(false);
    const [diaryData, setDiaryData] = useState<IDiaryListResult | null>(null);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [likeLoading, setLikeLoading] = useState<{ [key: number]: boolean }>({});
    const [activeKebab, setActiveKebab] = useState<number | null>(null);

    // 무한 스크롤을 위한 상태
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    useEffect(() => {
        // 초기 다이어리 목록 로드
        fetchDiaryData();
    }, []);

    // 초기 데이터 로드 함수
    const fetchDiaryData = async () => {
        try {
            setIsLoading(true);
            setCurrentPage(1);
            setHasMore(true);

            // 다이어리 목록 가져오기 (첫 페이지)
            const diaryListResponse = await getDiaryList({ page: 1 });

            // API 응답 구조 디버깅
            console.log("diaryListResponse:", diaryListResponse);

            // API 응답을 그대로 사용 (IDiaryListResult 구조)
            setDiaryData(diaryListResponse);

            // 다음 페이지가 있는지 확인
            setHasMore(diaryListResponse.pagination.hasNextPage);
        } catch (error) {
            console.error("다이어리 데이터 로드 실패:", error);
            // 에러 시 기본 데이터로 초기화
            setDiaryData({
                diaryList: [],
                pagination: {
                    currentPage: 1 as any,
                    totalCount: 0 as any,
                    totalPages: 0 as any,
                    hasNextPage: false,
                    hasPrevPage: false
                }
            });
            setHasMore(false);
        } finally {
            setIsLoading(false);
        }
    };

    // 추가 데이터 로드 함수 (무한 스크롤)
    const loadMoreData = useCallback(async () => {
        // 중복 호출 방지: 이미 로딩 중이거나 더 이상 데이터가 없으면 즉시 반환
        if (isLoadingMore || !hasMore || !diaryData) {
            console.log("🚫 loadMoreData 호출 차단:", { isLoadingMore, hasMore, hasData: !!diaryData });
            return;
        }

        try {
            console.log("🔄 추가 데이터 로드 시작:", { currentPage, hasMore, isLoadingMore });
            setIsLoadingMore(true);
            const nextPage = currentPage + 1;

            // 다음 페이지 데이터 가져오기
            const nextPageResponse = await getDiaryList({ page: nextPage });
            console.log("📄 다음 페이지 응답:", { nextPage, dataLength: nextPageResponse.diaryList.length });

            // 새 데이터가 있는지 확인
            if (nextPageResponse.diaryList.length === 0) {
                console.log("❌ 더 이상 데이터가 없음");
                setHasMore(false);
                return;
            }

            // 기존 데이터와 새 데이터 합치기
            setDiaryData(prevData => {
                if (!prevData) return nextPageResponse;

                const newData = {
                    ...prevData,
                    diaryList: [...prevData.diaryList, ...nextPageResponse.diaryList],
                    pagination: nextPageResponse.pagination
                };
                console.log("📊 데이터 병합 완료:", {
                    기존개수: prevData.diaryList.length,
                    새개수: nextPageResponse.diaryList.length,
                    총개수: newData.diaryList.length
                });
                return newData;
            });

            // 페이지 상태 업데이트
            setCurrentPage(nextPage);
            setHasMore(nextPageResponse.pagination.hasNextPage);
            console.log("✅ 페이지 상태 업데이트:", { nextPage, hasNextPage: nextPageResponse.pagination.hasNextPage });
        } catch (error) {
            console.error("❌ 추가 데이터 로드 실패:", error);
            // 에러 발생 시 더 이상 로드하지 않도록 설정
            setHasMore(false);
        } finally {
            setIsLoadingMore(false);
        }
    }, [currentPage, hasMore, isLoadingMore, diaryData]);

    // 스크롤 이벤트 감지 (무한 스크롤)
    useEffect(() => {
        let isScrolling = false; // 중복 스크롤 처리 방지 플래그
        let scrollTimeout: NodeJS.Timeout;

        const handleScroll = () => {
            // 이미 스크롤 처리 중이거나 로딩 중이거나 더 이상 데이터가 없으면 스킵
            if (isScrolling || isLoadingMore || !hasMore) {
                console.log("🚫 스크롤 감지 스킵:", { isScrolling, isLoadingMore, hasMore });
                return;
            }

            // 스크롤이 페이지 하단에 도달했는지 확인
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollPosition = scrollTop + windowHeight;
            const triggerPosition = documentHeight - 200;

            console.log("📏 스크롤 위치:", {
                scrollTop,
                windowHeight,
                documentHeight,
                scrollPosition,
                triggerPosition,
                shouldLoad: scrollPosition >= triggerPosition
            });

            // 스크롤이 하단 200px 이내에 도달하면 추가 데이터 로드
            if (scrollPosition >= triggerPosition) {
                console.log("🚀 무한 스크롤 트리거!");

                // 스크롤 처리 중 플래그 설정
                isScrolling = true;

                // loadMoreData 호출
                loadMoreData().finally(() => {
                    // API 호출 완료 후 플래그 해제 (1초 후)
                    clearTimeout(scrollTimeout);
                    scrollTimeout = setTimeout(() => {
                        isScrolling = false;
                        console.log("✅ 스크롤 처리 완료, 플래그 해제");
                    }, 1000);
                });
            }
        };

        // 스크롤 이벤트 리스너 추가 (throttle 적용)
        let ticking = false;
        const throttledHandleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', throttledHandleScroll);

        // 컴포넌트 언마운트 시 이벤트 리스너 제거
        return () => {
            window.removeEventListener('scroll', throttledHandleScroll);
            clearTimeout(scrollTimeout);
        };
    }, [isLoadingMore, hasMore, loadMoreData]);

    // 케밥 메뉴 외부 클릭 시 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (activeKebab !== null) {
                setActiveKebab(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [activeKebab]);

    // 좋아요 토글 함수
    const handleLikeToggle = async (diaryId: number, e: React.MouseEvent) => {
        e.stopPropagation(); // 이벤트 버블링 방지

        try {
            setLikeLoading(prev => ({ ...prev, [diaryId]: true }));

            // 현재 다이어리 상태 확인
            const currentDiary = diaryData?.diaryList?.find(d => d.id === diaryId);
            console.log("목록 좋아요 토글 전:", { diaryId, isLiked: currentDiary?.isLiked, likeCount: currentDiary?.likeCount });

            const result = await toggleDiaryLike(diaryId.toString());
            console.log("목록 API 응답:", result);

            // 다이어리 데이터 업데이트
            setDiaryData(prevData => {
                if (!prevData) return prevData;

                return {
                    ...prevData,
                    diaryList: prevData.diaryList.map(diary =>
                        diary.id === diaryId
                            ? {
                                ...diary,
                                isLiked: result.isLiked,
                                likeCount: result.likeCount
                            }
                            : diary
                    )
                };
            });

        } catch (error) {
            console.error("좋아요 토글 실패:", error);
            // TODO: 에러 메시지 표시 (팝업 또는 토스트)
        } finally {
            setLikeLoading(prev => ({ ...prev, [diaryId]: false }));
        }
    };

    // 다이어리 클릭 시 상세 화면으로 이동
    const handleDiaryClick = (diaryId: number) => {
        navigate(`/student/growth/diary/${diaryId}`);
    };



    // 이미지 클릭 시 팝업 표시
    const handleImageClick = (images: any[], initialIndex: number = 0) => {
        showPopup(
            <ImageSlidePopup
                imageInfoList={images.map(img => img.image)}
                initialSlideIdx={initialIndex}
                onClose={() => { }}
            />
        );
    };

    // 다이어리 삭제
    const handleDeleteDiary = async (diaryId: number) => {
        // 내가 작성한 다이어리인지 확인
        const diary = diaryData?.diaryList?.find(d => d.id === diaryId);
        if (!diary) return;

        console.log("diary", { diary });
        console.log("userInfo", { userInfo });

        if (diary.userId !== userInfo?.id) {
            alert("자신이 작성한 다이어리만 삭제할 수 있습니다.");
            return;
        }

        // 더 나은 확인 다이얼로그 사용
        const isConfirmed = window.confirm("정말로 이 다이어리를 삭제하시겠습니까?\n\n삭제된 다이어리는 복구할 수 없습니다.");

        if (isConfirmed) {
            try {
                // 다이어리 삭제 API 호출
                await deleteDiary(diaryId.toString());

                // 성공적으로 삭제된 경우 목록에서 제거
                setDiaryData(prevData => {
                    if (!prevData) return prevData;
                    return {
                        ...prevData,
                        diaryList: prevData.diaryList.filter(diary => diary.id !== diaryId)
                    };
                });

                // 케밥 메뉴 닫기
                setActiveKebab(null);

                // 성공 메시지 (더 나은 사용자 피드백)
                console.log("다이어리가 성공적으로 삭제되었습니다.");
                // TODO: 토스트 메시지나 스낵바로 대체 가능
            } catch (error) {
                console.error("다이어리 삭제 실패:", error);
                alert("다이어리 삭제에 실패했습니다. 다시 시도해주세요.");
            }
        }
    };



    // 다이어리 목록 스켈레톤 컴포넌트
    const DiaryListSkeleton = () => (
        <div className="space-y-6">
            {[1, 2, 3].map((index) => (
                <div key={index} className="bg-white rounded-2xl shadow-sm overflow-hidden animate-pulse">
                    {/* 다이어리 헤더 스켈레톤 */}
                    <div className="p-4 sm:p-6 border-b border-gray-100">
                        <div className="space-y-2">
                            {/* 첫 번째 줄: 제목과 포트폴리오 배지 스켈레톤 */}
                            <div className="flex items-center justify-between">
                                <div className="h-6 bg-gray-200 rounded w-48"></div>
                                <div className="h-5 bg-gray-200 rounded w-16"></div>
                            </div>
                            {/* 두 번째 줄: 날짜 스켈레톤 */}
                            <div className="flex items-center justify-between">
                                <div className="h-4 bg-gray-200 rounded w-24"></div>
                            </div>
                        </div>
                    </div>

                    {/* 다이어리 내용 스켈레톤 */}
                    <div className="p-4 sm:p-6">
                        {/* 내용 스켈레톤 */}
                        <div className="mb-4 space-y-2">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                        </div>

                        {/* 비디오 스켈레톤 */}
                        <div className="mb-4">
                            <div className="h-48 bg-gray-200 rounded-lg"></div>
                        </div>

                        {/* 이미지 스켈레톤 */}
                        <div className="mb-4">
                            <div className="flex gap-2">
                                <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0"></div>
                            </div>
                        </div>

                        {/* 메타 정보 스켈레톤 */}
                        <div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-6">
                                    {/* 좋아요 스켈레톤 */}
                                    <div className="flex items-center space-x-2">
                                        <div className="w-5 h-5 bg-gray-200 rounded"></div>
                                        <div className="h-4 bg-gray-200 rounded w-8"></div>
                                    </div>
                                    {/* 댓글 스켈레톤 */}
                                    <div className="flex items-center space-x-2">
                                        <div className="w-5 h-5 bg-gray-200 rounded"></div>
                                        <div className="h-4 bg-gray-200 rounded w-8"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <main className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* 헤더 - 항상 표시 */}
                <div className="mb-8">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.txtTitle")}
                    </h1>
                    <p className="text-gray-600 text-sm sm:text-base">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.txtDesc")}
                    </p>
                    {/* <p className="text-pink-500 text-sm sm:text-base font-medium mt-2 animate-pulse">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.txtNotice")}
                    </p> */}
                </div>

                {/* 다이어리 작성 입력란 - 다이어리가 있을 때만 표시 */}
                {diaryData && diaryData.diaryList && diaryData.diaryList.length > 0 && (
                    <div className="mb-8">
                        <div
                            onClick={() => navigate("/student/growth/diary/write")}
                            className="bg-white border-2 border-gray-300 hover:border-[#CF3688] rounded-xl p-4 cursor-pointer transition-all duration-200 hover:shadow-md focus-within:ring-2 focus-within:ring-[#CF3688]/20 focus-within:border-[#CF3688]"
                        >
                            <div className="flex items-center gap-3">
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                </svg>
                                <span className="text-gray-400 text-base">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.txtWritePlaceholder")}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* 다이어리 목록 */}
                {isLoading || !diaryData ? (
                    <DiaryListSkeleton />
                ) : diaryData?.diaryList?.length === 0 ? (
                    /* 빈 상태 화면 */
                    <div className="flex flex-col items-center justify-center py-16 px-4">
                        <div className="text-center max-w-md">
                            {/* 메인 메시지 */}
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.txtEmptyState")}
                            </h3>
                            
                            {/* 설명 메시지 */}
                            <p className="text-gray-500 text-sm mb-8 leading-relaxed">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.txtEmptyStateDesc")}
                            </p>
                            
                            {/* 작성하기 버튼 */}
                            <button
                                onClick={() => navigate("/student/growth/diary/write")}
                                className="inline-flex items-center px-6 py-3 bg-[#CF3688] text-white font-medium rounded-xl hover:bg-[#CF3688]/90 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                </svg>
                                {getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.btnWrite")}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {diaryData?.diaryList?.map((entry: IDiary) => {
                            // 디버깅: 각 entry의 구조 확인
                            console.log('다이어리 entry:', entry);
                            console.log('이미지 개수:', entry.diaryImages?.length);
                            console.log('비디오 개수:', entry.diaryVideos?.length);

                            return (
                                <div
                                    key={entry.id}
                                    className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200"
                                >
                                    {/* 다이어리 헤더 */}
                                    <div className="p-4 sm:p-6 border-b border-gray-100">
                                        <div className="space-y-2">
                                            {/* 첫 번째 줄: 제목과 케밥 버튼 */}
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-lg sm:text-xl font-bold text-gray-900 break-words overflow-hidden">
                                                    {entry.title}
                                                </h3>

                                                {/* 케밥 버튼 */}
                                                {/* <div className="relative">
                                                    <button
                                                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setActiveKebab(activeKebab === entry.id ? null : entry.id);
                                                        }}
                                                        aria-label="다이어리 메뉴"
                                                    >
                                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                                            <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                                        </svg>
                                                    </button> */}

                                                {/* 케밥 툴팁 */}
                                                {/* {activeKebab === entry.id && (
                                                        <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-20 min-w-[140px] py-1">
                                                            <button
                                                                className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 transition-colors duration-200 flex items-center gap-3 group"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteDiary(entry.id);
                                                                }}
                                                            >
                                                                <svg className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-2 2v3M4 7h16" />
                                                                </svg>
                                                                <span className="font-medium">삭제</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div> */}
                                            </div>

                                            {/* 두 번째 줄: 날짜와 포트폴리오 여부 */}
                                            <div className="flex items-center justify-between">
                                                <p className="text-sm text-gray-500">
                                                    {new Date(entry.createdAt).toLocaleDateString('ko-KR')}
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    {/* 숨겨진 상태 표시 아이콘 */}
                                                    {(entry as any).isHidden && (
                                                        <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-600 rounded-full text-xs font-medium">
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                                                            </svg>
                                                            숨김
                                                        </div>
                                                    )}
                                                    {entry.isPortfolio && (
                                                        <div className="flex items-center gap-1 px-2 py-1 bg-[#CF3688]/10 text-[#CF3688] rounded-full text-xs font-medium">
                                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            Portfolio
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 다이어리 내용 */}
                                    <div className="p-4 sm:p-6">
                                        <div className="mb-4">
                                            <p className="text-gray-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words overflow-hidden">
                                                {entry.content}
                                            </p>
                                        </div>

                                        {/* 비디오 (우선 출력) */}
                                        {entry.diaryVideos && entry.diaryVideos.length > 0 && (
                                            <div className="mb-4">
                                                {entry.diaryVideos.map((diaryVideo) => {
                                                    return (
                                                        <div key={diaryVideo.id} className="relative">
                                                            <VideoPlayer
                                                                video={diaryVideo.video}
                                                                className="w-full rounded-lg border border-gray-200"
                                                                onVideoEnd={() => {
                                                                    // 비디오 재생 종료 시 콜백 (필요시 구현)
                                                                }}
                                                                onVideoError={(error) => {
                                                                    console.error('비디오 에러:', error);
                                                                }}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* 이미지 (좌우 스크롤, 정사각형) */}
                                        {entry.diaryImages && entry.diaryImages.length > 0 && (
                                            <div className="mb-4">
                                                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                                    {entry.diaryImages.map((diaryImage, index) => (
                                                        <div
                                                            key={diaryImage.id}
                                                            className="flex-shrink-0 aspect-square w-32 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                                                            onClick={() => handleImageClick(entry.diaryImages, index)}
                                                        >
                                                            <img
                                                                src={getImageUrl(diaryImage.image)}
                                                                alt={`다이어리 이미지 ${diaryImage.sequence}`}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* 통계 정보 */}
                                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                                                {/* 좋아요 버튼 */}
                                                <button
                                                    className="flex items-center space-x-1 hover:opacity-80 transition-opacity"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleLikeToggle(entry.id, e);
                                                    }}
                                                >
                                                    <svg
                                                        className={`w-4 h-4 ${entry.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}`}
                                                        fill={entry.isLiked ? '#CF3688' : 'none'}
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                    </svg>
                                                    <span className={entry.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}>
                                                        {entry.likeCount || 0}
                                                    </span>
                                                </button>

                                                {/* 댓글 수 */}
                                                {entry.commentCount !== undefined && (
                                                    <div className="flex items-center space-x-1">
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                        </svg>
                                                        <span>{entry.commentCount}</span>
                                                    </div>
                                                )}

                                                {/* 북마크 수 */}
                                                <div className="flex items-center space-x-1">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                                    </svg>
                                                    <span>{(entry as any).bookmarkCount || 0}</span>
                                                </div>
                                            </div>

                                            {/* 자세히 보기 버튼 */}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDiaryClick(entry.id);
                                                }}
                                                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-[#CF3688] hover:text-[#CF3688]/80 hover:bg-[#CF3688]/5 rounded-lg transition-all duration-200 font-medium"
                                            >
                                                <span>{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.btnViewDetail")}</span>
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {/* 무한 스크롤 로딩 인디케이터 */}
                        {isLoadingMore && (
                            <div className="flex justify-center items-center py-8">
                                <div className="flex items-center space-x-2">
                                    <svg className="w-5 h-5 animate-spin text-[#CF3688]" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {/* <span className="text-sm text-gray-500">더 많은 포스트를 불러오는 중...</span> */}
                                </div>
                            </div>
                        )}

                        {/* 더 이상 데이터가 없을 때 표시 */}
                        {!hasMore && diaryData?.diaryList?.length > 0 && (
                            <div className="flex justify-center items-center py-8">
                                <div className="text-center">
                                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    {/* <p className="text-gray-500 text-sm">모든 포스트를 불러왔습니다</p> */}
                                </div>
                            </div>
                        )}
                    </div>
                )}

            </div>

        </main>
    );
};

export default GrowthDiary;
